import { messaging } from '../config/firebase';
import * as apiService from './api';

class FCMNotificationService {
  constructor() {
    this.token = null;
    this.tokenRefreshInterval = null;
  }

  /**
   * Request notification permission
   * @returns {Promise<boolean>} - True if permission granted
   */
  async requestPermission() {
    if (!messaging) {
      console.warn('FCM not supported in this browser');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Wait for service worker to be ready
   * @returns {Promise<void>}
   */
  async waitForServiceWorker() {
    if ('serviceWorker' in navigator) {
      // Wait for service worker to be registered
      const registration = await navigator.serviceWorker.ready;
      console.log('✅ Service Worker ready:', registration);

      // Additional wait to ensure service worker is fully active and ready for push
      // Sometimes the service worker is "ready" but not yet handling push messages
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return registration;
    }
    return null;
  }

  /**
   * Get FCM token
   * @returns {Promise<string|null>} - FCM token or null
   */
  async getFCMToken() {
    if (!messaging) {
      console.error(
        '❌ FCM messaging is null - Firebase not initialized properly'
      );
      return null;
    }

    try {
      console.log('🔑 Getting FCM token...');
      console.log(
        'VAPID Key exists:',
        !!process.env.REACT_APP_FIREBASE_VAPID_KEY
      );

      // Wait for service worker to be ready
      await this.waitForServiceWorker();

      const token = await messaging.getToken({
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      });

      console.log('✅ FCM token obtained:', token ? 'Yes' : 'No');
      return token;
    } catch (error) {
      console.error('❌ Error getting FCM token:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack,
      });
      return null;
    }
  }

  /**
   * Check if notification permission is granted
   * @returns {boolean}
   */
  isPermissionGranted() {
    return Notification.permission === 'granted';
  }

  /**
   * Initialize FCM: Request permission and get token
   * @returns {Promise<boolean>} - True if successful
   */
  async initialize() {
    try {
      // Check if permission is granted
      if (!this.isPermissionGranted()) {
        console.warn('Notification permission not granted yet');
        return false;
      }

      console.log('✅ Permission already granted, getting FCM token...');

      // Get and register token
      const token = await this.getFCMToken();
      if (token) {
        this.token = token;
        await this.registerToken();

        // Handle token refresh
        this.handleTokenRefresh();

        return true;
      }

      return false;
    } catch (error) {
      console.error('Error initializing FCM:', error);
      return false;
    }
  }

  /**
   * Register FCM token with backend
   * @returns {Promise<boolean>}
   */
  async registerToken() {
    if (!this.token) {
      console.warn('No FCM token to register');
      return false;
    }

    try {
      console.log('🔐 Registering FCM token with backend...');
      await apiService.api.registerFCMToken(this.token);
      console.log('✅ FCM token registered successfully');

      // Note: Backend handles duplicate tokens gracefully
      // If token already exists, it will be updated rather than created

      return true;
    } catch (error) {
      console.error('Error registering FCM token:', error);
      return false;
    }
  }

  /**
   * Unregister FCM token from backend
   * @returns {Promise<boolean>}
   */
  async unregisterToken() {
    if (!this.token) {
      console.warn('No FCM token to unregister');
      return false;
    }

    try {
      await apiService.api.unregisterFCMToken(this.token);
      console.log('✅ FCM token unregistered successfully');

      // Clear token refresh interval
      if (this.tokenRefreshInterval) {
        clearInterval(this.tokenRefreshInterval);
        this.tokenRefreshInterval = null;
      }

      return true;
    } catch (error) {
      console.error('Error unregistering FCM token:', error);
      return false;
    }
  }

  /**
   * Handle token refresh
   */
  async handleTokenRefresh() {
    if (!messaging) return;

    // Check for token changes periodically
    this.tokenRefreshInterval = setInterval(async () => {
      try {
        const currentToken = await this.getFCMToken();
        if (currentToken && currentToken !== this.token) {
          console.log('🔄 FCM token changed, updating backend');
          // Unregister old token if it exists
          if (this.token) {
            await apiService.api.unregisterFCMToken(this.token);
          }
          // Register new token
          this.token = currentToken;
          await apiService.api.registerFCMToken(currentToken);
        }
      } catch (error) {
        console.error('Error handling token refresh:', error);
      }
    }, 60000); // Check every minute
  }

  /**
   * Setup foreground message listener
   * @param {Function} callback - Callback to handle foreground messages
   */
  setupForegroundMessageListener(callback) {
    if (!messaging) {
      console.warn('FCM not supported in this browser');
      return;
    }

    messaging.onMessage((payload) => {
      console.log('Message received (foreground):', payload);
      if (callback) {
        callback(payload);
      }
    });
  }

  /**
   * Get current FCM token
   * @returns {string|null}
   */
  getCurrentToken() {
    return this.token;
  }

  /**
   * Get notification preferences from backend
   * @returns {Promise<Object|null>}
   */
  async getNotificationPreferences() {
    try {
      const response = await apiService.api.getNotificationPreferences();
      console.log('Full API response:', response);

      // Backend now explicitly indicates if preferences have been set
      if (response.hasSetPreferences === false) {
        console.log('Preferences not set by user yet, returning null');
        return null;
      }

      // User has explicitly set preferences
      if (response.preferences) {
        return response.preferences;
      }

      return null;
    } catch (error) {
      console.error('Error getting notification preferences:', error);
      return null;
    }
  }

  /**
   * Update notification preferences
   * @param {Object} preferences - Preferences object { push, email }
   * @returns {Promise<boolean>}
   */
  async updateNotificationPreferences(preferences) {
    try {
      await apiService.api.updateNotificationPreferences(preferences);
      console.log('✅ Notification preferences updated');
      return true;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      return false;
    }
  }

  /**
   * Reset notification preferences to defaults
   * @returns {Promise<boolean>}
   */
  async resetNotificationPreferences() {
    try {
      await apiService.api.resetNotificationPreferences();
      console.log('✅ Notification preferences reset to defaults');
      return true;
    } catch (error) {
      console.error('Error resetting notification preferences:', error);
      return false;
    }
  }
}

// Export singleton instance
export default new FCMNotificationService();
